import React from 'react';
import useAutoId from 'components/hooks/useAutoId';
import { Override } from 'types';
import classNames from 'classnames';
import './SwitchInput.scss';

type InputProps = React.ComponentPropsWithoutRef<'input'>;
type LabelProps = React.ComponentPropsWithoutRef<'label'>;

type BaseProps = {
  name?: InputProps['name'];
  value?: InputProps['value'];
  checked?: InputProps['checked'];
  onChange?: InputProps['onChange'];
  disabled?: boolean;
  required?: boolean;
  label?: React.ReactNode;
  startLabel?: React.ReactNode;
  endLabel?: React.ReactNode;
  labelProps?: LabelProps;
  inputProps?: InputProps;
  kind?: 'default' | 'primary' | 'secondary' | 'white';
  variant?: 'default' | 'simple';
  srOnly?: boolean;
};

export type SwitchInputProps = Override<React.ComponentPropsWithoutRef<'span'>, BaseProps>;

function SwitchInput({
  name,
  value,
  checked,
  onChange,
  disabled,
  required,
  label,
  kind = 'secondary',
  variant,
  inputProps,
  labelProps,
  children,
  startLabel,
  endLabel,
  srOnly,
  ...props
}: SwitchInputProps): JSX.Element {
  const inputId = useAutoId(inputProps?.id, props.id);

  const rootClasses = classNames('SwitchInput', props.className, {
    [`is-${kind}`]: Boolean(kind),
    [`is-${variant}`]: Boolean(variant),
    'is-srOnly': srOnly,
  });

  return (
    <span {...props} className={rootClasses}>
      <input
        {...inputProps}
        value={value}
        checked={checked}
        onChange={onChange}
        type="checkbox"
        name={name}
        id={inputId}
        disabled={disabled}
        required={required}
      />
      {label || startLabel || children ? (
        <label
          {...labelProps}
          htmlFor={inputId}
          className={classNames('SwitchInput-startLabel', labelProps?.className)}
        >
          <span className="SwitchInput-labelContent">
            {label}
            {startLabel}
            {children}
          </span>
        </label>
      ) : null}
      {endLabel ? (
        <label
          {...labelProps}
          htmlFor={inputId}
          className={classNames('SwitchInput-endLabel', labelProps?.className)}
        >
          <span className="SwitchInput-labelContent">{endLabel}</span>
        </label>
      ) : null}
    </span>
  );
}

export default SwitchInput;
